import React, { useRef, useState, useEffect } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from '@chakra-ui/react';
import { getOrder, getVoucher, getAppSettings } from '../api';
import resumeForPdf from '../utils/resumeForPdf';

const options = { weekday: 'long', month: 'long', day: 'numeric' };
const optionsWithTime = {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
};

const logo = '/images/'+process.env.REACT_APP_PROJECT_ID+'/logo-scontrino.jpeg';

export default function PrintQrCode({ qrValue, text1, text2: orderId, withPreview, onPrint }) {
  const [preview, setPreview] = useState(null);
  const qrContainerRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [orderInfo, setOrderInfo] = useState(null);

  // Raccogli le info per l'ordine
  const _getOrder = async (_orderId) => {
    setIsLoading(true);
    
    const o = await getOrder(_orderId);
    
    // o.voucher è il code del voucher
    if (o.voucher) {
      const res = await getVoucher(o.voucher);
      o.voucherInfo = res.data;
    }
    const { showLunchDinner } = await getAppSettings();
    o.showLunchDinner = showLunchDinner;
    setIsLoading(false);
    o.resumeForPdf = resumeForPdf(o.order);
    
    o.createdAt = new Intl.DateTimeFormat('it', options).format(new Date(o.createdAt));

    if (o.selectedDate) {
      const _date = new Date(o.selectedDate);
      let selectedDate = o.showLunchDinner
        ? new Intl.DateTimeFormat('it', options).format(_date)
        : new Intl.DateTimeFormat('it', optionsWithTime).format(_date);
      let pranzoCena = _date.getHours() < 16 ? 'pranzo' : 'cena';
      o.selectedDate = o.showLunchDinner ? `${selectedDate} (${pranzoCena})` : selectedDate;
    }
    setOrderInfo(o);
    // console.log('orderInfo', o);
  };

  useEffect(() => {
    if (!orderId) return;
    _getOrder(orderId);
  }, [orderId]);


  const generateQRandPrint = () => {
    if (!orderInfo) {
      alert('Caricamento informazioni ordine in corso...');
      return;
    }

    const width = 300;
    const qrSize = 240;
    const logoSize = 240;
    const textPadding = 10; // padding sinistro
    const maxTextWidth = width - (textPadding * 2); // larghezza disponibile per il testo
    
    // Calcola l'altezza necessaria per i dettagli ordine
    // Stimiamo che ogni item possa occupare fino a 2-3 righe in caso di testo lungo
    const itemsCount = orderInfo?.resumeForPdf?.length || 0;
    const detailsHeight = 40 + (itemsCount * 32) + 30 + 20; // titolo + items (con margine per righe multiple) + totale + voucher
    
    // Aumenta l'altezza per contenere logo + testo + QR code + informazioni ordine + dettagli
    // Logo 240px + spazio 20px + testo ~100px + QR 240px + spazio 20px + dettagli
    const height = logoSize + 20 + 100 + qrSize + 20 + detailsHeight + 50;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    // Funzione helper per dividere il testo su più righe se supera la larghezza disponibile
    const wrapText = (text, maxWidth, ctx) => {
      const words = text.split(' ');
      const lines = [];
      let currentLine = words[0];

      for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const testLine = currentLine + ' ' + word;
        const metrics = ctx.measureText(testLine);
        
        if (metrics.width > maxWidth && currentLine.length > 0) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }
      lines.push(currentLine);
      return lines;
    };

    // Sfondo bianco
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);

    // Carica e disegna il logo
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Disegna il logo centrato in alto
      const logoX = (canvas.width - logoSize) / 2;
      ctx.drawImage(img, logoX, 10, logoSize, logoSize);

      // Testo sotto il logo (logo finisce a y=10+240=250, quindi testo inizia a y=260)
      ctx.fillStyle = '#000';
      ctx.font = '20px monospace';
      ctx.fillText(text1, textPadding, 270); 
      ctx.font = '14px monospace';
      ctx.fillText(orderId, textPadding, 290);
      ctx.fillText(`Creato ${orderInfo.createdAt}`, textPadding, 310);

      // Trova il canvas del QR code renderizzato da React
      const qrCanvasElem = qrContainerRef.current?.querySelector('canvas');
      
      if (qrCanvasElem) {
        // Centra il QR code orizzontalmente, posizionato dopo il testo (createdAt finisce a y=310, QR inizia a y=330)
        const qrStartY = 330;
        const x = (canvas.width - qrSize) / 2;
        ctx.drawImage(qrCanvasElem, x, qrStartY, qrSize, qrSize);
      } else {
        alert('QR code non trovato!');
        return;
      }

      // Informazioni ordine sotto il QR code
      let yPos = 580;
      ctx.font = '14px monospace';
      ctx.textAlign = 'center';
      
      // selectedDate
      if (orderInfo.selectedDate) {
        ctx.fillText(`${orderInfo.selectedDate}`, width / 2, yPos);
        yPos += 25;
      }

      // Dettagli ordine
      ctx.textAlign = 'left';
      ctx.font = '16px monospace';
      ctx.fillText('Dettagli ordine', textPadding, yPos);
      yPos += 20;

      ctx.font = '12px monospace';
      if (orderInfo.resumeForPdf && orderInfo.resumeForPdf.length > 0) {
        orderInfo.resumeForPdf.forEach((item) => {
          const free = item.free ? `+ ${item.free}` : '';
          const text = `${item.quantity} ${free} - ${item.title} (${item.totalPrice} €)`;
          const lines = wrapText(text, maxTextWidth, ctx);
          lines.forEach((line) => {
            ctx.fillText(line, textPadding, yPos);
            yPos += 16;
          });
        });
      }

      // Totale
      if (orderInfo.resumeForPdf && orderInfo.resumeForPdf.length > 0) {
        const totale = orderInfo.resumeForPdf.reduce((sum, item) => {
          return sum + item.totalPrice;
        }, 0);
        const totaleText = `Totale: ${totale} €`;
        const totaleLines = wrapText(totaleText, maxTextWidth, ctx);
        totaleLines.forEach((line) => {
          ctx.fillText(line, textPadding, yPos);
          yPos += 16;
        });
        yPos += 4; // spazio extra dopo il totale
      }

      // Voucher info
      if (orderInfo.voucherInfo) {
        const voucherText = `Voucher usati ${orderInfo.voucherInfo.value}${
          orderInfo.voucherInfo.type === 'euro' ? ' €' : '%'
        }`;
        const voucherLines = wrapText(voucherText, maxTextWidth, ctx);
        voucherLines.forEach((line) => {
          ctx.fillText(line, textPadding, yPos);
          yPos += 16;
        });
      }

      // Pagato con
      if (orderInfo.paymentType) {
        const paymentText = `Pagamento ${orderInfo.paymentType}`;
        const paymentLines = wrapText(paymentText, maxTextWidth, ctx);
        paymentLines.forEach((line) => {
          ctx.fillText(line, textPadding, yPos);
          yPos += 16;
        });
      }

      // Spazio
      yPos += 16;

      // Email
      ctx.fillText(process.env.REACT_APP_EMAIL, textPadding, yPos);

      // Preview
      const dataUrl = canvas.toDataURL('image/png');
      setPreview(dataUrl);
      // Stampa RawBT
      const base64 = dataUrl.replace(/^data:image\/png;base64,/, '');
      const uri = `rawbt:data:image/png;base64,${base64}`;
      window.open(uri, '_blank');
      if (onPrint) onPrint();
    };
    img.src = logo;
  };

  return (
    <div>
      <Button isLoading={isLoading} onClick={generateQRandPrint}>Stampa</Button>
      <div
        ref={qrContainerRef}
        style={{
          position: 'absolute',
          left: -9999,
          top: -9999,
          width: 0,
          height: 0,
          overflow: 'hidden',
        }}
        aria-hidden='true'
      >
        <QRCodeCanvas value={qrValue} size={120} level='M' includeMargin={true} />
      </div>
      {withPreview && preview && (
        <div
          style={{
            margin: '20px auto',
            display: 'inline-block',
            border: '1px solid #ccc',
            background: '#fff',
          }}
        >
          <img 
            src={preview} 
            alt='Anteprima' 
            style={{ 
              width: 300, 
              height: 'auto',
              maxHeight: 800 
            }} 
          />
        </div>
      )}
    </div>
  );
}
